import React from 'react';
import {
  Box, Typography, Chip, Collapse, IconButton, TableCell, TableRow, Tooltip,
  Paper, Button, Divider,
} from '@mui/material';
import {
  ExpandMore, ExpandLess, Security as SecurityIcon, Error as ErrorIcon,
  Warning as WarningIcon, Info as InfoIcon, LocationOn as LocationIcon,
  CarCrash as AccidentIcon, Phone as PhoneIcon, Shield as ShieldIcon,
  BugReport as BugIcon, CheckCircle as CheckIcon, Cancel as CancelIcon,
} from '@mui/icons-material';
import { Persona, Equipo } from './IdentidadAuditoria';

/**
 * One incident = one row.
 *
 * The flat audit list emits a dozen rows for a single accident (dispatch
 * records, four status-callback hops per call, one SMS row per contact,
 * the confirmation, the summary). Reading it meant mentally re-joining
 * rows by eventId. This renders the incident as a single line with the
 * outcome already resolved, and keeps every underlying entry available
 * as a chronological timeline underneath.
 */

const LEVEL_CONFIG: Record<string, { color: any; icon: React.ReactNode; label: string }> = {
  info: { color: 'info', icon: <InfoIcon fontSize="small" />, label: 'Info' },
  warn: { color: 'warning', icon: <WarningIcon fontSize="small" />, label: 'Warn' },
  security: { color: 'error', icon: <SecurityIcon fontSize="small" />, label: 'Security' },
  error: { color: 'error', icon: <ErrorIcon fontSize="small" />, label: 'Error' },
};

const CATEGORY_CONFIG: Record<string, { icon: React.ReactNode; label: string }> = {
  safezone: { icon: <LocationIcon fontSize="small" />, label: 'SafeZone' },
  accident: { icon: <AccidentIcon fontSize="small" />, label: 'Accidente' },
  sinch: { icon: <PhoneIcon fontSize="small" />, label: 'Telefonía' },
  security: { icon: <ShieldIcon fontSize="small" />, label: 'Seguridad' },
  error: { icon: <BugIcon fontSize="small" />, label: 'Error' },
};

// Spanish labels for the worker's action identifiers. Anything not
// listed falls back to the raw action name, so a new action added in the
// worker shows up readably-ish instead of disappearing.
const ACTION_LABELS: Record<string, string> = {
  processed: 'Evento procesado',
  call_dispatched: 'Llamada enviada',
  call_status_callback: 'Estado de llamada',
  sms_dispatched: 'SMS enviado',
  admin_alert_dispatched: 'Alerta al operador',
  owner_confirmed_ok: 'Dueño confirmó que está bien',
  owner_gather_no_confirm: 'Sin confirmación del dueño',
  family_cascade_suppressed: 'Cascada a contactos suprimida',
  contacts_rejected: 'Contactos inválidos descartados',
  no_contactable_numbers: 'Sin números contactables',
  telephony_unconfigured: 'Telefonía sin configurar',
  config_unavailable_no_userid: 'Configuración no disponible',
  engine_stop_sent: 'Apagado de motor enviado',
  engine_stop_skipped: 'Apagado de motor omitido',
  enriched_push_failed: 'Push enriquecido falló',
  event_received_no_safezone: 'Evento sin SafeZone activa',
  webhook_unauthorized: 'Webhook no autorizado',
  gather_callback_unauthorized: 'Callback DTMF no autorizado',
  twilio_callback_unauthorized: 'Callback Twilio no autorizado',
  event_validation_failed: 'Validación de evento falló',
  unhandled_exception: 'Excepción no controlada',
};

function timeAgo(date: string | Date): string {
  const diff = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
  if (diff < 60) return `hace ${diff}s`;
  if (diff < 3600) return `hace ${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)}h`;
  return `hace ${Math.floor(diff / 86400)}d`;
}

function formatDate(date: string | Date): string {
  return new Date(date).toLocaleString('es-DO', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

function clockTime(date: string | Date): string {
  return new Date(date).toLocaleTimeString('es-DO', {
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

/** Compact per-entry line: the two or three fields that matter for that action. */
function entryHighlight(action: string, d: any): string {
  if (!d) return '';
  switch (action) {
    case 'call_dispatched':
      return [
        d.isOwner ? 'dueño' : (d.contactName || 'contacto'),
        d.toNumber,
        d.dispatched ? 'aceptada por Twilio' : `rechazada (${d.twilioErrorCode ?? '?'})`,
        d.twilioErrorMessage,
      ].filter(Boolean).join(' · ');
    case 'call_status_callback':
      return [
        d.callStatus,
        d.answeredBy ? `AMD: ${d.answeredBy}` : null,
        d.durationSec != null ? `${d.durationSec}s` : null,
        d.reachedHuman ? 'habló una persona' : null,
        d.errorCode ? `error ${d.errorCode}` : null,
      ].filter(Boolean).join(' · ');
    case 'sms_dispatched':
      return [
        d.isOwner ? 'dueño' : (d.contactName || 'contacto'),
        d.toNumber,
        d.success ? 'entregado a Twilio' : `falló (${d.twilioErrorCode ?? '?'})`,
        d.twilioErrorMessage,
      ].filter(Boolean).join(' · ');
    case 'admin_alert_dispatched':
      return [
        d.toNumber,
        d.callDispatched ? 'llamada ok' : `llamada falló (${d.callErrorCode ?? '?'})`,
        d.smsDispatched ? 'SMS ok' : `SMS falló (${d.smsErrorCode ?? '?'})`,
      ].filter(Boolean).join(' · ');
    case 'owner_confirmed_ok':
    case 'owner_gather_no_confirm':
      return [
        d.digits ? `tecla ${d.digits}` : 'sin tecla',
        d.outcome,
        d.familyCascadeSuppressed ? 'contactos NO alertados' : null,
      ].filter(Boolean).join(' · ');
    case 'engine_stop_sent':
      return [d.traccarSuccess ? 'aceptado por Traccar' : 'rechazado', d.traccarError,
        d.position?.speedKmh != null ? `${d.position.speedKmh} km/h` : null].filter(Boolean).join(' · ');
    case 'engine_stop_skipped':
      return [d.skipReason, d.speedKmh != null ? `${d.speedKmh} km/h` : null].filter(Boolean).join(' · ');
    case 'processed':
      return [
        d.isPremium ? 'PREMIUM' : 'FREE',
        d.actionTaken,
        d.requiresManualReview ? 'requiere revisión manual' : null,
        d.position?.address,
      ].filter(Boolean).join(' · ');
    case 'contacts_rejected':
      return `${d.count} número(s) descartados`;
    default:
      return [d.reason, d.skipReason, d.error?.message, d.hint].filter(Boolean).join(' · ');
  }
}

interface Props {
  group: any;
  expanded: boolean;
  onToggle: () => void;
  onShowRaw: (entry: any) => void;
}

const AuditGroupRow: React.FC<Props> = ({ group, expanded, onToggle, onShowRaw }) => {
  const g = group;
  const s = g.summary || {};

  const bg =
    g.level === 'security' ? 'rgba(211, 47, 47, 0.04)'
    : g.level === 'error' ? 'rgba(255, 152, 0, 0.04)'
    : undefined;

  const durationLabel =
    g.durationMs > 1000 ? `${Math.round(g.durationMs / 1000)}s` : null;

  return (
    <>
      <TableRow hover sx={{ backgroundColor: bg, cursor: 'pointer' }} onClick={onToggle}>
        <TableCell width={40}>
          <IconButton size="small">{expanded ? <ExpandLess /> : <ExpandMore />}</IconButton>
        </TableCell>

        <TableCell>
          <Tooltip title={formatDate(g.lastTimestamp)}>
            <Typography variant="body2" noWrap>{timeAgo(g.lastTimestamp)}</Typography>
          </Tooltip>
          {durationLabel && (
            <Typography variant="caption" color="text.secondary">duró {durationLabel}</Typography>
          )}
        </TableCell>

        <TableCell>
          <Chip size="small" label={LEVEL_CONFIG[g.level]?.label || g.level}
            color={LEVEL_CONFIG[g.level]?.color || 'default'}
            icon={LEVEL_CONFIG[g.level]?.icon as any} />
        </TableCell>

        <TableCell>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {(g.categories || []).map((c: string) => (
              <Chip key={c} size="small" variant="outlined"
                label={CATEGORY_CONFIG[c]?.label || c}
                icon={CATEGORY_CONFIG[c]?.icon as any} />
            ))}
          </Box>
        </TableCell>

        <TableCell sx={{ maxWidth: 380 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>{g.title}</Typography>
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.5 }}>
            {s.ownerConfirmed && (
              <Chip size="small" color="success" variant="outlined" icon={<CheckIcon fontSize="small" />}
                label="Dueño OK" />
            )}
            {s.reachedHuman && (
              <Chip size="small" color="success" variant="outlined" label="Contactado" />
            )}
            {s.callsDispatched > 0 && (
              <Chip size="small" variant="outlined" label={`${s.callsDispatched} llam.`} />
            )}
            {s.smsSent > 0 && <Chip size="small" variant="outlined" label={`${s.smsSent} SMS`} />}
            {s.smsFailed > 0 && (
              <Chip size="small" color="error" variant="outlined" icon={<CancelIcon fontSize="small" />}
                label={`${s.smsFailed} SMS fallidos`} />
            )}
            {(s.providerErrors || []).map((c: any) => (
              <Chip key={String(c)} size="small" color="error" variant="outlined" label={`cod ${c}`} />
            ))}
            {s.requiresManualReview && (
              <Chip size="small" color="warning" variant="outlined" label="Revisión manual" />
            )}
          </Box>
        </TableCell>

        <TableCell>
          <Persona id={g.userId} nombre={g.userName} correo={g.userEmail} />
        </TableCell>
        <TableCell>
          <Equipo id={g.deviceId} nombre={g.deviceName} imei={g.deviceImei} />
        </TableCell>
        <TableCell align="center">
          <Chip size="small" label={g.count} />
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={8} sx={{ py: 0, borderBottom: expanded ? undefined : 'none' }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 1, my: 1 }}>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Event ID</Typography>
                  <Typography variant="body2" fontFamily="monospace">{g.eventId ?? '-'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Inicio</Typography>
                  <Typography variant="body2">{formatDate(g.firstTimestamp)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Fin</Typography>
                  <Typography variant="body2">{formatDate(g.lastTimestamp)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Worker</Typography>
                  <Typography variant="body2">{g.workerVersion ?? '-'}</Typography>
                </Box>
                {s.address && (
                  <Box sx={{ flex: 1, minWidth: 220 }}>
                    <Typography variant="caption" color="text.secondary">Ubicación</Typography>
                    <Typography variant="body2">{s.address}</Typography>
                  </Box>
                )}
              </Box>

              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                Línea de tiempo ({g.timeline?.length || 0} pasos)
              </Typography>

              {/* Hand-rolled timeline: a left rail with a dot per step.
                  Avoids @mui/lab's beta Timeline API. */}
              <Box sx={{ position: 'relative', pl: 3 }}>
                <Box sx={{
                  position: 'absolute', left: 7, top: 6, bottom: 6,
                  width: '2px', backgroundColor: 'divider',
                }} />
                {(g.timeline || []).map((e: any, i: number) => {
                  const dotColor =
                    e.level === 'error' || e.level === 'security' ? 'error.main'
                    : e.level === 'warn' ? 'warning.main'
                    : 'primary.main';
                  return (
                    <Box key={e._id || i} sx={{ position: 'relative', pb: 1.5 }}>
                      <Box sx={{
                        position: 'absolute', left: -21, top: 5, width: 12, height: 12,
                        borderRadius: '50%', backgroundColor: dotColor,
                        border: '2px solid', borderColor: 'background.paper',
                      }} />
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="caption" fontFamily="monospace" color="text.secondary">
                          {clockTime(e.timestamp)}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {ACTION_LABELS[e.action] || e.action}
                        </Typography>
                        {(e.level === 'error' || e.level === 'security' || e.level === 'warn') && (
                          <Chip size="small" label={LEVEL_CONFIG[e.level]?.label}
                            color={LEVEL_CONFIG[e.level]?.color} sx={{ height: 18, fontSize: '0.65rem' }} />
                        )}
                        <Button size="small" sx={{ minWidth: 0, p: 0, fontSize: '0.7rem' }}
                          onClick={(ev) => { ev.stopPropagation(); onShowRaw(e); }}>
                          json
                        </Button>
                      </Box>
                      {entryHighlight(e.action, e.details) && (
                        <Typography variant="caption" color="text.secondary"
                          sx={{ display: 'block', wordBreak: 'break-word' }}>
                          {entryHighlight(e.action, e.details)}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>

              {(s.errors || []).length > 0 && (
                <Paper variant="outlined" sx={{ p: 1.5, mt: 1, borderColor: 'error.light' }}>
                  <Typography variant="caption" color="error">
                    Pasos con error: {(s.errors || []).join(', ')}
                  </Typography>
                </Paper>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export default AuditGroupRow;
